import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft,
  Calendar,
  X,
  Loader2,
  Upload,
  Search,
  Check
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Link } from 'react-router-dom';

export default function CreateEvent() {
  const navigate = useNavigate();
  const { user, profile, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [timezoneSearch, setTimezoneSearch] = useState('');
  const [showTimezoneDropdown, setShowTimezoneDropdown] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    timezone: 'America/New_York',
    agenda: '',
    objectives: '',
    critical_notes: '',
    status: 'planning',
    google_drive_url: ''
  });

  useEffect(() => {
    if (authLoading) return;
    if (profile && profile.role !== 'admin') {
      navigate(createPageUrl('Events'));
    }
  }, [authLoading, profile, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-zinc-800 rounded w-48"></div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
              <div className="h-4 bg-zinc-800 rounded w-32"></div>
              <div className="h-10 bg-zinc-800 rounded"></div>
              <div className="h-10 bg-zinc-800 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('events')
        .insert({
          ...formData,
          created_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;

      if (avatarFile && data?.id) {
        const fileExt = avatarFile.name.split('.').pop();
        const fileName = `${data.id}/avatar.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('event-avatars')
          .upload(fileName, avatarFile);

        if (!uploadError) {
          const { data: urlData } = supabase.storage
            .from('event-avatars')
            .getPublicUrl(fileName);

          await supabase
            .from('events')
            .update({ avatar_url: urlData.publicUrl })
            .eq('id', data.id);
        }
      }

      navigate(createPageUrl('EventPortal') + `?eventId=${data.id}&section=overview`);
    } catch (error: any) {
      console.error('Error creating event:', error);
      setError(error?.message || 'Failed to create event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const timezones = [
    { value: 'America/New_York', label: 'New York (EST/EDT)', region: 'Americas' },
    { value: 'America/Chicago', label: 'Chicago (CST/CDT)', region: 'Americas' },
    { value: 'America/Denver', label: 'Denver (MST/MDT)', region: 'Americas' },
    { value: 'America/Phoenix', label: 'Phoenix (MST)', region: 'Americas' },
    { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)', region: 'Americas' },
    { value: 'America/Anchorage', label: 'Anchorage (AKST/AKDT)', region: 'Americas' },
    { value: 'Pacific/Honolulu', label: 'Honolulu (HST)', region: 'Americas' },
    { value: 'America/Toronto', label: 'Toronto (EST/EDT)', region: 'Americas' },
    { value: 'America/Vancouver', label: 'Vancouver (PST/PDT)', region: 'Americas' },
    { value: 'America/Mexico_City', label: 'Mexico City (CST/CDT)', region: 'Americas' },
    { value: 'America/Sao_Paulo', label: 'São Paulo (BRT/BRST)', region: 'Americas' },
    { value: 'America/Buenos_Aires', label: 'Buenos Aires (ART)', region: 'Americas' },
    { value: 'America/Lima', label: 'Lima (PET)', region: 'Americas' },
    { value: 'America/Bogota', label: 'Bogotá (COT)', region: 'Americas' },
    { value: 'Europe/London', label: 'London (GMT/BST)', region: 'Europe' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)', region: 'Europe' },
    { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)', region: 'Europe' },
    { value: 'Europe/Rome', label: 'Rome (CET/CEST)', region: 'Europe' },
    { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)', region: 'Europe' },
    { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)', region: 'Europe' },
    { value: 'Europe/Brussels', label: 'Brussels (CET/CEST)', region: 'Europe' },
    { value: 'Europe/Vienna', label: 'Vienna (CET/CEST)', region: 'Europe' },
    { value: 'Europe/Stockholm', label: 'Stockholm (CET/CEST)', region: 'Europe' },
    { value: 'Europe/Copenhagen', label: 'Copenhagen (CET/CEST)', region: 'Europe' },
    { value: 'Europe/Warsaw', label: 'Warsaw (CET/CEST)', region: 'Europe' },
    { value: 'Europe/Prague', label: 'Prague (CET/CEST)', region: 'Europe' },
    { value: 'Europe/Athens', label: 'Athens (EET/EEST)', region: 'Europe' },
    { value: 'Europe/Istanbul', label: 'Istanbul (TRT)', region: 'Europe' },
    { value: 'Europe/Moscow', label: 'Moscow (MSK)', region: 'Europe' },
    { value: 'Europe/Dublin', label: 'Dublin (GMT/IST)', region: 'Europe' },
    { value: 'Europe/Lisbon', label: 'Lisbon (WET/WEST)', region: 'Europe' },
    { value: 'Asia/Dubai', label: 'Dubai (GST)', region: 'Asia' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)', region: 'Asia' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)', region: 'Asia' },
    { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)', region: 'Asia' },
    { value: 'Asia/Shanghai', label: 'Shanghai (CST)', region: 'Asia' },
    { value: 'Asia/Seoul', label: 'Seoul (KST)', region: 'Asia' },
    { value: 'Asia/Bangkok', label: 'Bangkok (ICT)', region: 'Asia' },
    { value: 'Asia/Kolkata', label: 'Mumbai/Kolkata (IST)', region: 'Asia' },
    { value: 'Asia/Karachi', label: 'Karachi (PKT)', region: 'Asia' },
    { value: 'Asia/Jakarta', label: 'Jakarta (WIB)', region: 'Asia' },
    { value: 'Asia/Manila', label: 'Manila (PHT)', region: 'Asia' },
    { value: 'Asia/Taipei', label: 'Taipei (CST)', region: 'Asia' },
    { value: 'Asia/Jerusalem', label: 'Jerusalem (IST/IDT)', region: 'Asia' },
    { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)', region: 'Pacific' },
    { value: 'Australia/Melbourne', label: 'Melbourne (AEDT/AEST)', region: 'Pacific' },
    { value: 'Australia/Brisbane', label: 'Brisbane (AEST)', region: 'Pacific' },
    { value: 'Australia/Perth', label: 'Perth (AWST)', region: 'Pacific' },
    { value: 'Pacific/Auckland', label: 'Auckland (NZDT/NZST)', region: 'Pacific' },
    { value: 'Pacific/Fiji', label: 'Fiji (FJT)', region: 'Pacific' },
    { value: 'Africa/Cairo', label: 'Cairo (EET)', region: 'Africa' },
    { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)', region: 'Africa' },
    { value: 'Africa/Lagos', label: 'Lagos (WAT)', region: 'Africa' },
    { value: 'Africa/Nairobi', label: 'Nairobi (EAT)', region: 'Africa' },
  ];

  const filteredTimezones = timezones.filter(tz =>
    tz.label.toLowerCase().includes(timezoneSearch.toLowerCase()) ||
    tz.value.toLowerCase().includes(timezoneSearch.toLowerCase())
  );

  const selectedTimezone = timezones.find(tz => tz.value === formData.timezone);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <Link to={createPageUrl('Events')} className="inline-flex items-center text-sm text-zinc-500 hover:text-white transition-colors mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Events
        </Link>
        <h1 className="text-2xl font-bold text-white">Create New Event</h1>
        <p className="text-zinc-500 mt-1">Set up your event production portal</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Basic Information</h2>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300">Event Avatar</Label>
              <div className="mt-1.5 flex items-center gap-4">
                {avatarPreview ? (
                  <div className="relative">
                    <img
                      src={avatarPreview}
                      alt="Event avatar"
                      className="w-20 h-20 rounded-lg object-cover border border-zinc-700"
                    />
                    <button
                      type="button"
                      onClick={removeAvatar}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-zinc-800 hover:bg-zinc-700 rounded-full flex items-center justify-center border border-zinc-600 transition-colors"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                    <Upload className="w-6 h-6 text-zinc-500" />
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    id="avatar"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                  <label htmlFor="avatar">
                    <Button
                      type="button"
                      variant="ghost"
                      className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                      onClick={() => document.getElementById('avatar')?.click()}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {avatarPreview ? 'Change Avatar' : 'Upload Avatar'}
                    </Button>
                  </label>
                  <p className="text-xs text-zinc-500 mt-1">PNG, JPG up to 5MB</p>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="name" className="text-zinc-300">Event Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Annual Tech Conference 2025"
                required
                className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_date" className="text-zinc-300">Start Date</Label>
                <div className="relative mt-1.5">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="pl-10 bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="end_date" className="text-zinc-300">End Date</Label>
                <div className="relative mt-1.5">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="pl-10 bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="timezone" className="text-zinc-300">Timezone</Label>
              <div className="relative mt-1.5">
                <div
                  onClick={() => setShowTimezoneDropdown(!showTimezoneDropdown)}
                  className="relative w-full cursor-pointer"
                >
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type="text"
                    value={showTimezoneDropdown ? timezoneSearch : selectedTimezone?.label || ''}
                    onChange={(e) => {
                      setTimezoneSearch(e.target.value);
                      setShowTimezoneDropdown(true);
                    }}
                    onClick={() => {
                      setShowTimezoneDropdown(true);
                      setTimezoneSearch('');
                    }}
                    placeholder="Search timezone or city..."
                    className="pl-10 bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500"
                  />
                </div>

                {showTimezoneDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => {
                        setShowTimezoneDropdown(false);
                        setTimezoneSearch('');
                      }}
                    />
                    <div className="absolute top-full left-0 right-0 mt-2 max-h-80 overflow-y-auto bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-20">
                      {filteredTimezones.length === 0 ? (
                        <div className="px-4 py-8 text-center text-zinc-500 text-sm">
                          No timezones found
                        </div>
                      ) : (
                        filteredTimezones.map((tz) => (
                          <button
                            key={tz.value}
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, timezone: tz.value });
                              setShowTimezoneDropdown(false);
                              setTimezoneSearch('');
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-zinc-800 transition-colors flex items-center justify-between group"
                          >
                            <div className="flex flex-col">
                              <span className="text-white text-sm font-medium">{tz.label}</span>
                              <span className="text-zinc-500 text-xs">{tz.value}</span>
                            </div>
                            {formData.timezone === tz.value && (
                              <Check className="w-4 h-4 text-primary-500" />
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="status" className="text-zinc-300">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="planning">Planning</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Event Details</h2>
          <div className="space-y-4">
            <div>
              <Label htmlFor="agenda" className="text-zinc-300">High-Level Agenda</Label>
              <Textarea
                id="agenda"
                value={formData.agenda}
                onChange={(e) => setFormData({ ...formData, agenda: e.target.value })}
                placeholder="Brief overview of the event schedule..."
                rows={3}
                className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500"
              />
            </div>

            <div>
              <Label htmlFor="objectives" className="text-zinc-300">Key Objectives</Label>
              <Textarea
                id="objectives"
                value={formData.objectives}
                onChange={(e) => setFormData({ ...formData, objectives: e.target.value })}
                placeholder="What are the main goals of this event?"
                rows={3}
                className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500"
              />
            </div>

            <div>
              <Label htmlFor="critical_notes" className="text-zinc-300">Critical Notes</Label>
              <Textarea
                id="critical_notes"
                value={formData.critical_notes}
                onChange={(e) => setFormData({ ...formData, critical_notes: e.target.value })}
                placeholder="Important information everyone should know..."
                rows={3}
                className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500"
              />
            </div>

            <div>
              <Label htmlFor="google_drive_url" className="text-zinc-300">Google Drive Folder</Label>
              <Input
                id="google_drive_url"
                type="url"
                value={formData.google_drive_url}
                onChange={(e) => setFormData({ ...formData, google_drive_url: e.target.value })}
                placeholder="https://drive.google.com/drive/folders/..."
                className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500"
              />
              <p className="text-xs text-zinc-500 mt-1.5">Shared Google Drive folder for event files</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4">
          <Link to={createPageUrl('Events')}>
            <Button type="button" variant="ghost" className="text-zinc-400 hover:text-white">
              Cancel
            </Button>
          </Link>
          <Button
            type="submit"
            disabled={loading}
            className="bg-white text-zinc-900 hover:bg-zinc-200"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Create Event
          </Button>
        </div>
      </form>
    </div>
  );
}
